import { NgForOf, NgIf } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter, Host,
  Input,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { first, pairwise } from 'rxjs/operators';
import { UtilsService } from '../../../../shared/services/utils.service';
import { DataRulesDataService } from '../../../data-rules-data.service';
import { AllPrimaryEntitiesNames, DataRuleId } from '../../../data-rules-model';
import { ISavedDataRuleEventData } from '../../../data-rules.component';
import { ICustomOverlay, ICustomPositioningConfig } from '../../../lib/custom-component-overlay.directive';
import { ChainedRulesGroupComponent } from '../chained-rules-group/chained-rules-group.component';
import { IChainViewState } from '../chained-rules-model';
import { ChainData, ChainRuleData, ComplexChainData, RuleChainsDataService, } from '../rule-chain-data.service';

@Component({
  selector: 'app-chained-rule-groups',
  templateUrl: './chained-rule-groups-screen.component.html',
  imports: [
    ChainedRulesGroupComponent,
    NgIf,
    NgForOf
  ],
  styleUrls: ['./chained-rule-groups-screen.component.scss']
})
export class ChainedRuleGroupsScreenComponent implements OnInit, ICustomOverlay {
  @ViewChild(TemplateRef) templateRef: TemplateRef<any>;
  @Output() closed = new EventEmitter<void>();
  @Input() overlayPositioningConfig: ICustomPositioningConfig;

  @Host() nativeElement: ElementRef<ChainedRuleGroupsScreenComponent>;

  @Input() navigateToChains: (chains?: DataRuleId[]) => void;
  @Input() navigateToDataRules: (entity: AllPrimaryEntitiesNames, rules?: DataRuleId[]) => void;
  @Input() chainsTrigger: ElementRef<HTMLElement>;
  @Output() chainClickedEventEmitter = new EventEmitter<DataRuleId>();
  savedChainEvent: Subject<ISavedDataRuleEventData> = new Subject<ISavedDataRuleEventData>();

  routeParamChainIds: DataRuleId[];

  @ViewChild('popupComponent') popupComponent;
  @ViewChild('focusElement') focusElement: ElementRef;
  @ViewChildren(ChainedRulesGroupComponent)
  ruleChainComponents: QueryList<ChainedRulesGroupComponent>;

  private viewStateMap = new Map<DataRuleId, IChainViewState>();

  constructor(
    public ruleChainsDataService: RuleChainsDataService,
    private dataRulesDataService: DataRulesDataService,
    public utilsService: UtilsService,
    private route: ActivatedRoute
  ) {
  }

  ngOnInit(): void {
    forkJoin({
      chains: this.ruleChainsDataService.initialize(false),
      dataRules: this.dataRulesDataService.initialize(false)}
    ).subscribe(() => {
      this.route.queryParams.pipe(first()).subscribe(queryParams => this.onRouteChange(null, queryParams));
      this.route.queryParams.pipe(pairwise())
        .subscribe(([previousQueryParams, queryParams]) => this.onRouteChange(previousQueryParams, queryParams));
      this.savedChainEvent.subscribe((eventData: ISavedDataRuleEventData) => {
        this.onSavedChain(eventData);
      });
      this.ruleChainsDataService.allChains.forEach(chain => {
          if (!this.viewStateMap.has(chain.id))
            this.viewStateMap.set(chain.id, {dirty: false, expanded: false});
        }
      );
    });
  }

  public getViewState(chainId: DataRuleId): IChainViewState {
    return this.viewStateMap.has(chainId) ? this.viewStateMap.get(chainId) : {expanded: false, dirty: false};
  }

  public setViewState(chainId: DataRuleId, state: IChainViewState): void {
    this.viewStateMap.set(chainId, state);
  }

  getChainComplexData(chainId: DataRuleId): ComplexChainData {
    const chainData = this.ruleChainsDataService.getChain(chainId);
    return new ComplexChainData(
      chainData.id,
      chainData.name,
      chainData.ruleIds.map(ruleId => {
        const ruleData = this.dataRulesDataService.getRule(ruleId);
        return new ChainRuleData(
          ruleData.id,
          ruleData.name,
          ruleData.source?.config?.entity
        );
      })
    );
  }

  // additional subscription to reflect just chains related changes
  onRouteChange(previousParams, newParams): void {

    // 'chain' param expected to be undefined or '' or 'id,id,id...'-like string
    const routeChainParam = newParams['chain'];
    const areChainsOpen = typeof routeChainParam === 'string';
    this.routeParamChainIds = routeChainParam ? routeChainParam.split(',') : [];

    // required "opening click" for initial page loading
    if (!previousParams && areChainsOpen) {
          // this.chainsTrigger.nativeElement.click();
        let waiter = setInterval(() => {
          if (
            (!this.ruleChainsDataService.loading && this.ruleChainsDataService.isInitialisedOnce)
            && (!this.dataRulesDataService.loading && this.dataRulesDataService.isInitialisedOnce)) {
            this.chainsTrigger.nativeElement.click();
            clearInterval(waiter);
            waiter = undefined;
          }
        }, 100);
    }

    if (this.routeParamChainIds.length) {
      // redirect to collapsed state on demands
      const isAnyNonExistingChain = this.routeParamChainIds.some(id => this.ruleChainsDataService.getChainIndex(id) === -1
      );
      if (isAnyNonExistingChain) {
        this.navigateToChains();
        return;
      }

      this.proceedToNavigatedChains();
    } else {
      this.ruleChainsDataService.allChains.forEach(chain => {
        this.setViewState(chain.id, Object.assign(this.getViewState(chain.id), {expanded: false}));
      });
    }
  }

  proceedToNavigatedChains(): void {
    // collapse required-to rules if they are expanded
    this.ruleChainsDataService.allChains.forEach((chain) => {
      if (this.getViewState(chain.id).expanded) {
        if (this.routeParamChainIds.findIndex(
          paramChainId => this.utilsService.compareInternalDataRuleIds(paramChainId, chain.id)
        ) === -1) {
          this.setViewState(chain.id, Object.assign(this.getViewState(chain.id), {expanded: false}));
        }
      }
    });

    // expand required-to rules if they are collapsed
    this.routeParamChainIds
      .forEach(id => {
        const chain = this.ruleChainsDataService.getChain(id);
        if (chain) {
          this.setViewState(chain.id, Object.assign(this.getViewState(chain.id), {expanded: true}));
        }
      });

    // scrolling into the last rule from selected
    const chainIdToScrollInto = this.routeParamChainIds[this.routeParamChainIds.length - 1];
    setTimeout(() => {
      const chainComponent = this.ruleChainComponents.find(
        component => this.utilsService.compareInternalDataRuleIds(component.chainData.id, chainIdToScrollInto)
      );
      if (chainComponent) {
        // 500 milliseconds timeout required to await Angular to finish rendering this.rules QueryList ref components
        setTimeout(() => {
          chainComponent.chainReference.nativeElement.scrollIntoView({behavior: 'smooth', inline: 'center', block: 'center'});
        }, 500);
      }
    }, 0);
  }

  addNewChain() {
    const newChain: ChainData = new ChainData(0, '', []);
    this.ruleChainsDataService.createChain(newChain);
    this.onChainClicked(0);
  }

  onChainClicked(chainId: DataRuleId) {
    this.chainClickedEventEmitter.emit(chainId);
  }

  private onSavedChain(eventData: ISavedDataRuleEventData): void {
    const {savedId, unsavedId} = eventData;
    const routeParamChainsIds = this.routeParamChainIds;
    let newIds;
    if (`${unsavedId}` !== `${savedId}`) {
      newIds = routeParamChainsIds.map(id => `${id}` === '0' ? savedId : id);
      this.navigateToChains(newIds);
    }
  }

  onChainDeleted(id: DataRuleId): void {
    const newIds = this.routeParamChainIds;
    const deletedIndex = this.routeParamChainIds.findIndex(paramId => this.utilsService.compareInternalDataRuleIds(paramId, id));
    if (deletedIndex !== -1) {
      newIds.splice(deletedIndex, 1);
    }
    this.navigateToChains(newIds);
  }
}
